import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { CheckCircle, Circle, Clock, AlertCircle, RefreshCw, ArrowLeft, Activity, Layers, Download, ChevronDown, ChevronRight, Search } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTopology } from './TopologyContext';
import { useData } from './DataContext';
import { StallBadge, WarningBadge, ConvergenceBadge, FabricBadge } from './components/primitives';
import { JobReport, parseConfig } from './jobs/JobReport';
import { ConvergenceChart, LiftDragRatioChart, SingleSeriesChart, ForceBreakdownChart, PointProbeChart, SpanwiseChartBlock } from './jobs/charts';

// ===== Stage definitions (4-stage PowerFlow pipeline) =====
const STAGE_NAMES = ['Discretization', 'Decomposition', 'Simulation', 'Post-Processing'];

// ===== Helpers =====
function formatNumber(n) {
  if (n == null) return '—';
  return n.toLocaleString();
}

function formatEta(seconds) {
  if (seconds == null || seconds < 0) return '—';
  if (seconds < 60) return `${seconds}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// Format an elapsed seconds count as "1h 23m" / "4m 12s" / "12s".
function formatElapsed(seconds) {
  if (seconds == null || seconds < 0) return null;
  const s = Math.floor(seconds);
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s % 60}s`;
}

// Live ticker — re-renders once a second while a stage is running, so the
// "running 1h 23m" label keeps ticking up without needing a parent re-render.
function useNow(enabled) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [enabled]);
  return now;
}

// ===== Current-step callout =====
// Big, readable banner above the pipeline bar so it's obvious which stage a
// job is on without having to decode the color-coded segments.
export function CurrentStepCallout({ stages, live, compact = false }) {
  const byName = useMemo(() => {
    const m = {};
    (stages || []).forEach(s => { m[s.name] = s; });
    return m;
  }, [stages]);

  // Live SSE feed wins; falls back to the parser's verdict.
  const liveStage = live?.currentStage;
  let activeName = liveStage
    || STAGE_NAMES.find(n => byName[n]?.status === 'running')
    || null;

  const allCompleted = STAGE_NAMES.every(n => byName[n]?.status === 'completed');
  const allPending = STAGE_NAMES.every(n => !byName[n] || byName[n].status === 'pending');

  let label, idx, color, pulse;
  if (allCompleted) {
    label = 'Completed'; idx = 4; color = '#22c55e'; pulse = false;
  } else if (activeName) {
    label = activeName;
    idx = STAGE_NAMES.indexOf(activeName) + 1;
    color = '#22d3ee'; pulse = true;
  } else if (allPending) {
    label = 'Queued'; idx = 0; color = '#9ca3af'; pulse = false;
  } else {
    const lastDone = [...STAGE_NAMES].reverse().find(n => byName[n]?.status === 'completed');
    label = lastDone ? `${lastDone} done — waiting for next` : 'Waiting';
    idx = lastDone ? STAGE_NAMES.indexOf(lastDone) + 1 : 0;
    color = '#9ca3af'; pulse = false;
  }

  const activeStage = activeName ? byName[activeName] : null;
  const stageProgress = activeStage?.progress ?? null;
  const subStatus = activeStage?.subStatus || null;
  const activeSince = activeStage?.activeSince || null;

  // Tick once per second so the elapsed time updates live while running.
  const now = useNow(pulse && !!activeSince);
  const elapsed = activeSince ? formatElapsed(now / 1000 - activeSince) : null;

  // Per-stage one-liner. For Simulation, the sub-step replaces the generic hint.
  const stepHint = activeName === 'Simulation' && subStatus
    ? subStatusHint(subStatus)
    : ({
        Discretization:   'Preparing case file and mesh',
        Decomposition:    'Splitting domain across processors',
        Simulation:       'Running CFD solver',
        'Post-Processing':'Writing results and visualizations',
      }[activeName] || null);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: compact ? '6px 10px' : '8px 12px', borderRadius: 8,
      backgroundColor: 'rgba(34,211,238,0.06)',
      border: `1px solid ${color}33`,
    }}>
      <span style={{
        width: 9, height: 9, borderRadius: '50%', backgroundColor: color,
        boxShadow: pulse ? `0 0 8px ${color}` : 'none',
        animation: pulse ? 'pulse 1.4s ease-in-out infinite' : 'none',
        flexShrink: 0,
      }} />
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Current step{idx > 0 && idx <= 4 ? ` ${idx} of 4` : ''}
          </span>
          <span style={{ fontSize: compact ? 13 : 15, fontWeight: 700, color: '#fff' }}>
            {label}{subStatus ? ` → ${subStatus}` : ''}
          </span>
          {stageProgress != null && stageProgress > 0 && stageProgress < 100 && (
            <span style={{ fontSize: 12, color: '#22d3ee', fontFamily: 'monospace' }}>
              {stageProgress.toFixed(1)}%
            </span>
          )}
          {elapsed && (
            <span style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace', marginLeft: 'auto' }}>
              running {elapsed}
            </span>
          )}
        </div>
        {stepHint && !compact && (
          <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{stepHint}</span>
        )}
      </div>
    </div>
  );
}

function subStatusHint(s) {
  return {
    Initializing: 'MPI startup and case initialization',
    Solver:       'Time-stepping the CFD solver',
    Finalizing:   'Writing checkpoint and final output',
    Complete:     'Solver finished',
  }[s] || null;
}

// ===== PowerFLOW info panel — categorized fields parsed from the job dir =====
// Reads job.details (built server-side by read_job_details).
const INFO_SECTIONS = [
  { key: 'progress', label: 'Live Progress',  color: '#22d3ee' },
  { key: 'case',     label: 'Case Size',      color: '#3b82f6' },
  { key: 'solver',   label: 'Solver',         color: '#a78bfa' },
  { key: 'system',   label: 'System',         color: '#f59e0b' },
  { key: 'decomp',   label: 'Decomposition',  color: '#10b981' },
  { key: 'runtime',  label: 'PowerFLOW Build',color: '#9ca3af' },
  { key: 'files',    label: 'Output Files',   color: '#10b981' },
];


export function SpanwiseLoadsPanel({ spanwise, status, onGenerate, generating, genError }) {
  const aero = useMemo(() => (spanwise?.aero || []).slice().sort((a, b) => a.station - b.station), [spanwise]);
  // Structure rows share strip order with aero; borrow the % semi-span for a common X axis.
  const struct = useMemo(() => {
    const rows = spanwise?.structure || [];
    const withStation = rows.map((r, i) => ({ ...r, station: aero[i]?.station }));
    return withStation.filter((r) => Number.isFinite(r.station)).sort((a, b) => a.station - b.station);
  }, [spanwise, aero]);

  const canGenerate = status?.canGenerate;
  const available = spanwise?.available;
  if (!available && !canGenerate) return null;

  const aoa = status?.aoa != null ? `${status.aoa}°` : '—';
  const mach = status?.mach != null ? `M${status.mach}` : '';

  return (
    <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #1e293b' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Spanwise Load Distribution</span>
        <span style={{ fontSize: 11, color: '#6b7280' }}>{mach} · α={aoa} · exaritool / process_wing_rev09.sh</span>
      </div>

      {!available && canGenerate && (
        <div style={{
          marginTop: 10, padding: '12px 14px', borderRadius: 8,
          backgroundColor: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.3)',
          color: '#93c5fd', fontSize: 12,
        }}>
          <div style={{ marginBottom: 10 }}>
            Inputs present (mean_flow.snc, .cdi, wing-information files) — spanwise post-processing can be
            generated for this run at <b>α={aoa}</b>. This runs <code>process_wing_rev09.sh</code> and
            consumes a PowerFLOW license token; one run at a time.
          </div>
          <button
            onClick={onGenerate}
            disabled={generating || !onGenerate}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '7px 14px', borderRadius: 6, border: 'none',
              fontSize: 12, fontWeight: 600,
              cursor: generating ? 'default' : 'pointer',
              backgroundColor: generating ? '#1e293b' : '#2563eb',
              color: generating ? '#93c5fd' : '#fff',
            }}>
            {generating
              ? <><RefreshCw style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} /> Running post-proc…</>
              : <>Generate spanwise loads (α={aoa})</>}
          </button>
          {generating && (
            <span style={{ marginLeft: 10, color: '#6b7280', fontSize: 11 }}>
              extracting surfaces + per-strip forces — this can take a few minutes
            </span>
          )}
          {genError && (
            <div style={{ marginTop: 8, color: '#fca5a5', fontSize: 11 }}>
              {genError}
            </div>
          )}
        </div>
      )}

      {available && (
        <>
          <SpanwiseChartBlock label="Lift & Drag Coefficient (Cl, Cd)" color="#22d3ee" data={aero}
            series={[{ key: 'cl', name: 'Cl', color: '#22d3ee' }, { key: 'cd', name: 'Cd', color: '#f59e0b' }]} decimals={4} />
          <SpanwiseChartBlock label="Sectional Efficiency (Cl / Cd)" color="#34d399" data={aero}
            series={[{ key: 'clcd', name: 'Cl/Cd', color: '#34d399' }]} decimals={2} />
          <SpanwiseChartBlock label="Twist Distribution" color="#a78bfa" data={aero}
            series={[{ key: 'twist', name: 'Twist', color: '#a78bfa' }]} decimals={2} unit="°" />
          {struct.length > 1 && (
            <SpanwiseChartBlock label="Structural Load Distribution (full-scale)" color="#f472b6" data={struct}
              series={[{ key: 'lift', name: 'Lift (lb)', color: '#f472b6' }, { key: 'drag', name: 'Drag (lb)', color: '#facc15' }]} decimals={1} unit=" lb" />
          )}
        </>
      )}
    </div>
  );
}

function PowerFlowInfoPanel({ details, onGenerateSpanwise, generating, genError }) {
  if (!details) return null;
  const nonEmpty = INFO_SECTIONS.filter(s => {
    const d = details[s.key];
    return d && Object.keys(d).length > 0;
  });
  const warnings = Array.isArray(details.warnings) ? details.warnings : [];
  const conv = details.convergence;
  const hasConv = (conv?.lift?.length || 0) + (conv?.drag?.length || 0) > 0;
  const breakdown = details.forceBreakdown;
  const hasBreakdown = Array.isArray(breakdown) && breakdown.length > 0;
  const fh = details.forceHistory;
  const hasPitch = (fh?.pitchMoment?.length || 0) > 1;
  const hasSide = (fh?.sideForce?.length || 0) > 1;
  const hasRoll = (fh?.rollMoment?.length || 0) > 1;
  const hasYaw = (fh?.yawMoment?.length || 0) > 1;
  const pp = details.pointProbes;
  const hasProbes = (pp?.pressure?.length || 0) > 1 && Object.keys(pp?.labels || {}).length > 0;
  const spanwise = details.spanwiseLoads;
  const ppStatus = details.postprocStatus;
  const hasSpanwise = spanwise?.available || ppStatus?.canGenerate;
  if (nonEmpty.length === 0 && warnings.length === 0 && !hasConv && !hasBreakdown
      && !hasPitch && !hasSide && !hasRoll && !hasYaw && !hasProbes && !hasSpanwise) return null;

  return (
    <div style={{
      backgroundColor: '#0f172a', borderRadius: 12,
      border: '1px solid #1e293b', padding: 18,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Layers style={{ width: 16, height: 16, color: '#3b82f6' }} />
        <h3 style={{ color: '#fff', fontSize: 14, fontWeight: 600, margin: 0 }}>
          PowerFLOW Info
        </h3>
        <span style={{ color: '#6b7280', fontSize: 11 }}>
          parsed from simulator.o, decomposer.o, runscript, .exa_jobctl_status
        </span>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 12,
      }}>
        {nonEmpty.map(s => (
          <InfoSection key={s.key} label={s.label} color={s.color} data={details[s.key]} />
        ))}
      </div>
      {hasConv && <ConvergenceChart convergence={conv} />}
      {hasConv && <LiftDragRatioChart convergence={conv} status={details.convergenceStatus} />}
      {hasPitch && <SingleSeriesChart label="Pitching Moment (My)" color="#f472b6" series={fh.pitchMoment} decimals={2} />}
      {hasRoll && <SingleSeriesChart label="Rolling Moment (Mx)" color="#facc15" series={fh.rollMoment} decimals={2} />}
      {hasYaw && <SingleSeriesChart label="Yawing Moment (Mz)" color="#38bdf8" series={fh.yawMoment} decimals={2} />}
      {hasSide && <SingleSeriesChart label="Side Force (Fy)" color="#34d399" series={fh.sideForce} decimals={5} />}
      {hasBreakdown && <ForceBreakdownChart breakdown={breakdown} />}
      {hasProbes && <PointProbeChart label="Static Pressure at Probe Points" data={pp.pressure} labels={pp.labels} />}
      {hasProbes && <PointProbeChart label="Velocity Magnitude at Probe Points" data={pp.velocity} labels={pp.labels} />}
      {hasSpanwise && <SpanwiseLoadsPanel spanwise={spanwise} status={ppStatus}
        onGenerate={onGenerateSpanwise} generating={generating} genError={genError} />}
      {warnings.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
            color: '#facc15', fontSize: 12, fontWeight: 600,
          }}>
            <AlertCircle style={{ width: 14, height: 14 }} />
            Warnings ({warnings.length})
          </div>
          <ul style={{
            margin: 0, padding: '8px 12px', listStyle: 'none',
            backgroundColor: 'rgba(250,204,21,0.06)',
            border: '1px solid rgba(250,204,21,0.2)',
            borderRadius: 8, maxHeight: 200, overflowY: 'auto',
          }}>
            {warnings.map((w, i) => (
              <li key={i} style={{
                color: '#fde68a', fontSize: 11, fontFamily: 'monospace',
                padding: '4px 0', borderBottom: i < warnings.length - 1 ? '1px solid rgba(250,204,21,0.1)' : 'none',
                whiteSpace: 'normal', wordBreak: 'break-word',
              }}>
                {w}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function InfoSection({ label, color, data }) {
  return (
    <div style={{
      backgroundColor: '#0b1322', borderRadius: 10,
      border: `1px solid ${color}33`, padding: 12,
    }}>
      <div style={{
        fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
        color, textTransform: 'uppercase', marginBottom: 8,
      }}>
        {label}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {Object.entries(data).map(([k, v]) => (
          <div key={k} style={{
            display: 'flex', justifyContent: 'space-between', gap: 8,
            fontSize: 12, alignItems: 'baseline',
          }}>
            <span style={{ color: '#9ca3af', flexShrink: 0 }}>{k}</span>
            <span style={{
              color: '#fff', fontFamily: 'monospace', textAlign: 'right',
              wordBreak: 'break-word', minWidth: 0,
            }}>
              {String(v)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ===== 4-segment stage pipeline bar (used in list view) =====
function StagePipelineBar({ stages, compact = false }) {
  // Map each named stage to its status from the API. If a named stage isn't in the
  // backend response, we fall back to "pending".
  const byName = useMemo(() => {
    const m = {};
    (stages || []).forEach(s => { m[s.name] = s; });
    return m;
  }, [stages]);

  const segHeight = compact ? 6 : 10;
  const gap = 3;

  return (
    <div>
      <div style={{ display: 'flex', gap, marginBottom: compact ? 4 : 6 }}>
        {STAGE_NAMES.map((name, i) => {
          const stage = byName[name];
          const status = stage?.status || 'pending';
          const progress = stage?.progress ?? 0;
          let bg = '#374151';     // pending
          let fillBg = null;
          if (status === 'completed') bg = '#22c55e';
          else if (status === 'running') {
            bg = '#1e40af';
            fillBg = '#22d3ee';
          } else if (status === 'failed') bg = '#ef4444';

          return (
            <div key={name} style={{
              flex: 1, height: segHeight, borderRadius: segHeight / 2,
              backgroundColor: bg, position: 'relative', overflow: 'hidden',
              boxShadow: status === 'running' ? '0 0 6px rgba(34,211,238,0.6)' : 'none',
            }}>
              {fillBg && progress > 0 && (
                <div style={{
                  position: 'absolute', left: 0, top: 0, bottom: 0,
                  width: `${Math.min(progress, 100)}%`,
                  backgroundColor: fillBg,
                  transition: 'width 1s ease-out',
                }} />
              )}
            </div>
          );
        })}
      </div>
      {!compact && (
        <div style={{ display: 'flex', gap, fontSize: 9, color: '#6b7280' }}>
          {STAGE_NAMES.map(name => {
            const status = byName[name]?.status || 'pending';
            const color = status === 'running' ? '#22d3ee'
                        : status === 'completed' ? '#22c55e'
                        : status === 'failed' ? '#ef4444'
                        : '#6b7280';
            return (
              <div key={name} style={{
                flex: 1, textAlign: 'center', color,
                fontWeight: status === 'running' ? 700 : 400,
                textTransform: 'uppercase', letterSpacing: 0.5,
              }}>
                {name.length > 11 ? name.slice(0, 8) + '…' : name}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===== Cluster badge =====
function ClusterBadge({ node }) {
  const { CLUSTERS, NODE_TO_CLUSTER } = useTopology();
  const cid = NODE_TO_CLUSTER[node];
  const cluster = cid ? CLUSTERS[cid] : null;
  if (!cluster) return null;
  return (
    <span style={{
      fontSize: 10, fontWeight: 600,
      color: cluster.color,
      backgroundColor: `${cluster.color}1a`,
      padding: '2px 8px', borderRadius: 6, letterSpacing: 0.3,
    }}>{cluster.label}</span>
  );
}

// ==================== JOBS LIST VIEW ====================
const JobsList = ({ onSelectJob, onCacheUpdate, cachedJobs, liveData }) => {
  const [jobs, setJobs] = useState(cachedJobs || []);
  const [loading, setLoading] = useState((cachedJobs || []).length === 0);
  const [error, setError] = useState(null);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get('/api/jobs/running');
      const data = res.data;
      if (!data.success) {
        setError(data.error || 'Failed to load jobs');
        setLoading(false);
        return;
      }
      const activeJobs = (data.jobs || []).filter(job => {
        if (job.status === 'RUNNING') return true;
        const sim = (job.stages || []).find(s => s.name === 'Simulation' && s.status === 'running');
        if (!sim) return false;
        if (sim.metrics?.Timestep) return true;
        if (sim.startTime) {
          try {
            const days = (new Date() - new Date(sim.startTime)) / (1000 * 60 * 60 * 24);
            if (days <= 30) return true;
          } catch {}
        }
        return Object.keys(sim.metrics || {}).length > 0;
      });
      activeJobs.sort((a, b) => (b.overallProgress || 0) - (a.overallProgress || 0));
      setJobs(activeJobs);
      onCacheUpdate?.(activeJobs);
      setError(null);
    } catch (err) {
      setError('Failed to fetch jobs: ' + (err.response?.status || err.message));
    } finally {
      setLoading(false);
    }
  }, [onCacheUpdate]);

  useEffect(() => {
    if ((cachedJobs || []).length === 0) {
      fetchJobs();
    }
    const id = setInterval(fetchJobs, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Quick lookup for live data overlay
  const liveByPath = useMemo(() => {
    const m = {};
    (liveData || []).forEach(j => { if (j.path) m[j.path] = j; });
    return m;
  }, [liveData]);

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <RefreshCw style={{ width: 32, height: 32, margin: '0 auto', color: '#3b82f6', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#9ca3af', marginTop: 12 }}>Loading jobs…</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, backgroundColor: 'rgba(239,68,68,0.1)', borderRadius: 12, border: '1px solid #7f1d1d', color: '#fca5a5' }}>
        <AlertCircle style={{ width: 18, height: 18, display: 'inline', marginRight: 8 }} />
        {error}
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div style={{ padding: 64, textAlign: 'center', color: '#6b7280' }}>
        <Activity style={{ width: 48, height: 48, margin: '0 auto 12px', opacity: 0.3 }} />
        <p style={{ fontSize: 16 }}>No active simulations</p>
        <p style={{ fontSize: 12, marginTop: 4 }}>Jobs that start will appear here</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 600 }}>
          {jobs.length} Job{jobs.length !== 1 ? 's' : ''}
        </h2>
        <button
          onClick={fetchJobs}
          style={{ padding: 8, borderRadius: 8, border: 'none', backgroundColor: '#1f2937', cursor: 'pointer' }}
          title="Refresh"
        >
          <RefreshCw style={{ width: 14, height: 14, color: '#9ca3af' }} />
        </button>
      </div>

      {jobs.map((job, i) => {
        const live = liveByPath[job.path];
        const currentStage = live?.currentStage
          || (job.stages || []).find(s => s.status === 'running')?.name
          || 'Simulation';
        const cur = live?.currentTimestep ?? null;
        const tot = live?.totalTimestep ?? null;
        const eta = live?.etaSeconds ?? null;
        const rate = live?.stepsPerSecond ?? null;

        return (
          <div
            key={`${job.path}-${i}`}
            onClick={() => onSelectJob(job)}
            style={{
              backgroundColor: '#0f172a', borderRadius: 16,
              border: '1px solid #1e293b', padding: 18,
              cursor: 'pointer', transition: 'border-color 0.2s, transform 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#334155'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e293b'; }}
          >
            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
              <Layers style={{ width: 16, height: 16, color: '#3b82f6', flexShrink: 0 }} />
              <span style={{
                color: '#fff', fontWeight: 600, fontSize: 14,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                maxWidth: 'calc(100% - 220px)', minWidth: 200,
              }}>{job.jobName}</span>
              <ClusterBadge node={job.node} />
              <span style={{ color: '#6b7280', fontSize: 11, fontFamily: 'monospace' }}>{job.node}</span>
              <StallBadge lastActivityTs={job.lastActivityTs} status={job.status} />
              <WarningBadge count={job.details?.errorCount} />
              <FabricBadge fabric={job.details?.fabric} />
              <div style={{ flex: 1 }} />
              <span style={{
                fontSize: 16, fontWeight: 700,
                color: job.overallProgress >= 99 ? '#22c55e' : '#22d3ee',
              }}>{job.overallProgress?.toFixed(1) ?? 0}%</span>
            </div>

            {/* Current step callout */}
            <CurrentStepCallout stages={job.stages} live={live} />

            {/* Stage pipeline */}
            <StagePipelineBar stages={job.stages} compact={false} />

            {/* Live metrics row (only when live data is available) */}
            {live && currentStage === 'Simulation' && (cur !== null || rate !== null) && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12,
                marginTop: 12, paddingTop: 12, borderTop: '1px solid #1e293b',
                fontSize: 11,
              }}>
                <div>
                  <p style={{ color: '#6b7280', marginBottom: 2, fontSize: 10 }}>TIMESTEP</p>
                  <p style={{ color: '#fff', fontFamily: 'monospace' }}>
                    {formatNumber(cur)}{tot ? ` / ${formatNumber(tot)}` : ''}
                  </p>
                </div>
                <div>
                  <p style={{ color: '#6b7280', marginBottom: 2, fontSize: 10 }}>RATE</p>
                  <p style={{ color: rate ? '#d1d5db' : '#4b5563', fontFamily: 'monospace' }}>
                    {rate ? `${rate.toFixed(1)} steps/s` : 'measuring…'}
                  </p>
                </div>
                <div>
                  <p style={{ color: '#6b7280', marginBottom: 2, fontSize: 10 }}>ETA</p>
                  <p style={{ color: eta ? '#22d3ee' : '#4b5563', fontFamily: 'monospace' }}>
                    {formatEta(eta)}
                  </p>
                </div>
              </div>
            )}

            {/* Tap hint */}
            <p style={{
              color: '#475569', fontSize: 10, marginTop: 10, textAlign: 'right',
            }}>
              Click for stage details →
            </p>
          </div>
        );
      })}
    </div>
  );
};

// ==================== JOB DETAIL VIEW ====================
// Merge all of a job's time-series into one CSV (client-side so it works for
// live and recalled/archived jobs alike).
function buildJobCsv(job) {
  const det = job.details || {};
  const conv = det.convergence || {};
  const fh = det.forceHistory || {};
  const pp = det.pointProbes || {};
  const cols = new Map();
  const at = (t) => { if (!cols.has(t)) cols.set(t, {}); return cols.get(t); };
  const add = (series, name) => (series || []).forEach(([t, v]) => { at(t)[name] = v; });
  add(conv.lift, 'lift_Fz'); add(conv.drag, 'drag_Fx');
  add(fh.sideForce, 'side_Fy'); add(fh.pitchMoment, 'pitch_My');
  add(fh.rollMoment, 'roll_Mx'); add(fh.yawMoment, 'yaw_Mz');
  (pp.pressure || []).forEach(r => Object.entries(r).forEach(([k, v]) => { if (k !== 'ts') at(r.ts)['press_' + k] = v; }));
  (pp.velocity || []).forEach(r => Object.entries(r).forEach(([k, v]) => { if (k !== 'ts') at(r.ts)['vel_' + k] = v; }));
  const base = ['lift_Fz', 'drag_Fx', 'side_Fy', 'pitch_My', 'roll_Mx', 'yaw_Mz'];
  const probeCols = [...new Set([].concat(...[...cols.values()].map(c => Object.keys(c)))
    .filter(k => k.startsWith('press_') || k.startsWith('vel_')))].sort();
  const header = ['timestep', ...base, ...probeCols];
  const rows = [header.join(',')];
  [...cols.keys()].sort((a, b) => a - b).forEach(ts => {
    const c = cols.get(ts);
    rows.push([ts, ...base.map(k => c[k] ?? ''), ...probeCols.map(k => c[k] ?? '')].join(','));
  });
  return rows.join('\n') + '\n';
}
function downloadJobCsv(job) {
  const blob = new Blob([buildJobCsv(job)], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = (job.jobName || 'job').slice(0, 80) + '.csv';
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

const JobDetail = ({ job, onBack, liveData }) => {
  const { CLUSTERS, NODE_TO_CLUSTER } = useTopology();
  const [jobData, setJobData] = useState(job);
  const [error, setError] = useState(null);
  // Re-sync when a DIFFERENT job is selected while this view stays mounted (e.g.
  // an openJob deep-link). jobData is seeded from the prop only once, so without
  // this an in-place job swap would keep showing the previous job's data.
  useEffect(() => { setJobData(job); /* eslint-disable-next-line */ }, [job.path]);
  // Real-time deltas pushed by the /api/jobs/stream/<path> SSE endpoint.
  // The 30s detail poll still runs as a fallback / metadata refresh; the
  // stream just overrides the volatile fields (timestep, perf, warnings).
  const [streamState, setStreamState] = useState({
    timestep: null,
    stepsPerSec: null,
    warnings: [],
    lastEventAt: null,
    connected: false,
  });

  const fetchJobDetail = useCallback(async () => {
    // Archived (recalled) jobs carry their full snapshot already — the live
    // endpoint only knows about currently-running jobs, so don't re-fetch.
    if (job.fromArchive) return;
    if (!job.path) return;
    try {
      const res = await axios.get(`/api/jobs/${job.path.replace(/^[/]+/, "")}`);
      if (res.data?.success) {
        setJobData(res.data.job);
        setError(null);
      } else {
        setError(res.data?.error || 'Failed to load job detail');
      }
    } catch (err) {
      setError('Fetch error: ' + (err.response?.status || err.message));
    }
  }, [job.path, job.fromArchive]);

  useEffect(() => {
    if (job.fromArchive) return;
    fetchJobDetail();
    const id = setInterval(fetchJobDetail, 30000);
    return () => clearInterval(id);
  }, [fetchJobDetail, job.fromArchive]);

  // Spanwise load distribution is a cheap disk read (no NetCDF, no license) and,
  // unlike the rest of the snapshot, is often produced AFTER a job is archived.
  // So fetch it fresh on open for every job — including recalled/archived ones,
  // whose snapshot predates the post-proc outputs — and overlay onto details.
  const fetchSpanwise = useCallback(async () => {
    if (!job.path) return;
    try {
      const res = await axios.get(`/api/jobs/${job.path.replace(/^[/]+/, "")}/spanwise`);
      if (res.data?.success) {
        setJobData(prev => ({
          ...prev,
          details: {
            ...(prev.details || {}),
            spanwiseLoads: res.data.spanwiseLoads,
            postprocStatus: res.data.postprocStatus,
          },
        }));
      }
    } catch (_) { /* supplementary — never block the detail view on this */ }
  }, [job.path]);

  useEffect(() => { fetchSpanwise(); }, [fetchSpanwise]);

  // On-demand spanwise generation: POST to run process_wing_rev09.sh (consumes a
  // PowerFLOW license token), then poll /spanwise until the outputs land or the
  // run errors. Only one runs at a time server-side (429 -> busy).
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const genPollRef = useRef(null);
  useEffect(() => () => clearInterval(genPollRef.current), []);

  const generateSpanwise = useCallback(async () => {
    if (!job.path) return;
    const rel = job.path.replace(/^[/]+/, "");
    setGenError(null);
    setGenerating(true);
    try {
      const res = await axios.post(`/api/jobs/${rel}/spanwise/generate`);
      if (!res.data?.success && res.data?.status !== 'running') {
        const miss = res.data?.missing?.length ? ` (missing: ${res.data.missing.join(', ')})` : '';
        setGenError((res.data?.error || 'generate failed') + miss);
        setGenerating(false);
        return;
      }
      let ticks = 0;
      clearInterval(genPollRef.current);
      genPollRef.current = setInterval(async () => {
        ticks += 1;
        try {
          const r = await axios.get(`/api/jobs/${rel}/spanwise`);
          if (r.data?.success) {
            setJobData(prev => ({
              ...prev,
              details: {
                ...(prev.details || {}),
                spanwiseLoads: r.data.spanwiseLoads,
                postprocStatus: r.data.postprocStatus,
              },
            }));
            const g = r.data.generate || {};
            if (r.data.spanwiseLoads?.available) {
              setGenerating(false); clearInterval(genPollRef.current);
            } else if (g.status === 'error') {
              setGenError(g.error || 'post-processing failed'); setGenerating(false); clearInterval(genPollRef.current);
            }
          }
        } catch (_) { /* transient; keep polling */ }
        if (ticks > 240) { clearInterval(genPollRef.current); setGenerating(false); } // ~20 min cap
      }, 5000);
    } catch (err) {
      const d = err.response?.data;
      setGenError(d?.error || err.message);
      setGenerating(false);
    }
  }, [job.path]);

  // Open the SSE stream for as long as this detail view is mounted.
  // EventSource sends the auth cookie automatically (same-origin) so no
  // header plumbing is needed.
  useEffect(() => {
    if (job.fromArchive) return;  // no live stream for recalled/ended jobs
    if (!job.path) return;
    const url = `/api/jobs/stream/${job.path.replace(/^[/]+/, "")}`;
    const es = new EventSource(url, { withCredentials: true });

    const stamp = () => Date.now();
    es.addEventListener('snapshot', () => {
      setStreamState(s => ({ ...s, connected: true, lastEventAt: stamp() }));
    });
    es.addEventListener('timestep', (e) => {
      try {
        const d = JSON.parse(e.data);
        setStreamState(s => ({ ...s, timestep: d.timestep, lastEventAt: stamp() }));
      } catch {}
    });
    es.addEventListener('perf', (e) => {
      try {
        const d = JSON.parse(e.data);
        setStreamState(s => ({ ...s, stepsPerSec: d.stepsPerSec, lastEventAt: stamp() }));
      } catch {}
    });
    es.addEventListener('warning', (e) => {
      try {
        const d = JSON.parse(e.data);
        setStreamState(s => ({
          ...s,
          // Cap at 50 so a runaway warning stream doesn't bloat memory
          warnings: [...s.warnings.slice(-49), { text: d.text, ts: stamp() }],
          lastEventAt: stamp(),
        }));
      } catch {}
    });
    es.addEventListener('close', () => es.close());
    es.onerror = () => {
      // EventSource auto-reconnects on transient failures; flag visually.
      setStreamState(s => ({ ...s, connected: false }));
    };

    return () => es.close();
  }, [job.path, job.fromArchive]);

  // Polled live data (every 10s from /api/jobs/live), overridden by any
  // fresher SSE values that have arrived in streamState.
  const live = useMemo(() => {
    const polled = (liveData || []).find(j => j.path === jobData.path);
    if (!polled && streamState.timestep == null) return undefined;
    return {
      ...(polled || {}),
      currentTimestep: streamState.timestep ?? polled?.currentTimestep,
      stepsPerSecond: streamState.stepsPerSec ?? polled?.stepsPerSecond,
    };
  }, [liveData, jobData.path, streamState.timestep, streamState.stepsPerSec]);

  const cluster = NODE_TO_CLUSTER[jobData.node];
  const clusterDef = cluster ? CLUSTERS[cluster] : null;
  const currentStageName = live?.currentStage
    || jobData.stages?.find(s => s.status === 'running')?.name;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Back nav + PDF export (both hidden in the printed report) */}
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: 'none', color: '#9ca3af',
            fontSize: 13, cursor: 'pointer', padding: 0,
          }}
        >
          <ArrowLeft style={{ width: 16, height: 16 }} />
          Back to Jobs List
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => downloadJobCsv(jobData)}
            title="Download this job's time-series (forces, moments, probes) as CSV"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#0d1424', border: '1px solid #334155', color: '#d1d5db',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              padding: '8px 14px', borderRadius: 8,
            }}
          >
            <Download style={{ width: 15, height: 15 }} />
            Export CSV
          </button>
          <button
            onClick={() => window.print()}
            title="Generate a printable PDF report of this job (use your browser's Save as PDF)"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#1d4ed8', border: 'none', color: '#fff',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              padding: '8px 14px', borderRadius: 8,
            }}
          >
            <Download style={{ width: 15, height: 15 }} />
            Export PDF Report
          </button>
        </div>
      </div>

      {/* Screen dashboard view — hidden in the printed PDF report */}
      <div className="no-print" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header card */}
      <div style={{ backgroundColor: '#0f172a', borderRadius: 16, border: '1px solid #1e293b', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ flex: 1, minWidth: 300 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              <h1 style={{ color: '#fff', fontSize: 20, fontWeight: 700, margin: 0 }}>{jobData.jobName}</h1>
              {clusterDef && <ClusterBadge node={jobData.node} />}
              <span style={{ color: '#6b7280', fontSize: 12, fontFamily: 'monospace' }}>{jobData.node}</span>
              <StallBadge lastActivityTs={jobData.lastActivityTs} status={jobData.status} />
              <WarningBadge count={jobData.details?.errorCount} />
              <FabricBadge fabric={jobData.details?.fabric} />
              <ConvergenceBadge status={jobData.details?.convergenceStatus} />
            </div>
            <p style={{ color: '#6b7280', fontSize: 11, fontFamily: 'monospace', wordBreak: 'break-all', margin: 0 }}>
              {jobData.jobDirectory || jobData.path}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: '#22d3ee' }}>{jobData.overallProgress?.toFixed(1) ?? 0}%</div>
            <div style={{ color: '#6b7280', fontSize: 11 }}>Overall Progress</div>
          </div>
        </div>

        {/* Current step callout */}
        <div style={{ marginTop: 16 }}>
          <CurrentStepCallout stages={jobData.stages} live={live} />
        </div>

        {/* Pipeline summary at top */}
        <div style={{ marginTop: 4 }}>
          <StagePipelineBar stages={jobData.stages} compact={false} />
        </div>

        {/* Live simulation panel (headline metrics) — not for recalled jobs */}
        {!job.fromArchive && live && currentStageName === 'Simulation' && (
          <div style={{
            marginTop: 16, padding: 16, borderRadius: 12,
            background: 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(34,211,238,0.08))',
            border: '1px solid rgba(34,211,238,0.2)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', backgroundColor: '#22d3ee',
                animation: 'pulse-dot 1.5s ease-in-out infinite',
              }} />
              <span style={{ color: '#22d3ee', fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>LIVE — SIMULATION</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
              <div>
                <p style={{ color: '#6b7280', fontSize: 10, marginBottom: 4 }}>TIMESTEP</p>
                <p style={{
                  color: '#fff', fontSize: 24, fontWeight: 700, fontFamily: 'monospace',
                  margin: 0, lineHeight: 1.1,
                }}>
                  {formatNumber(live.currentTimestep)}
                </p>
                {live.totalTimestep && (
                  <p style={{ color: '#9ca3af', fontSize: 12, fontFamily: 'monospace', marginTop: 2 }}>
                    of {formatNumber(live.totalTimestep)}
                    {' '}({((live.currentTimestep / live.totalTimestep) * 100).toFixed(1)}%)
                  </p>
                )}
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: 10, marginBottom: 4 }}>RATE</p>
                <p style={{
                  color: live.stepsPerSecond ? '#fff' : '#4b5563',
                  fontSize: 24, fontWeight: 700, fontFamily: 'monospace', margin: 0, lineHeight: 1.1,
                }}>
                  {live.stepsPerSecond ? live.stepsPerSecond.toFixed(1) : '—'}
                </p>
                <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 2 }}>
                  {live.stepsPerSecond ? 'timesteps / sec' : 'measuring…'}
                </p>
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: 10, marginBottom: 4 }}>ETA</p>
                <p style={{
                  color: live.etaSeconds ? '#22d3ee' : '#4b5563',
                  fontSize: 24, fontWeight: 700, fontFamily: 'monospace', margin: 0, lineHeight: 1.1,
                }}>
                  {formatEta(live.etaSeconds)}
                </p>
                <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 2 }}>remaining</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div style={{ padding: 12, backgroundColor: 'rgba(239,68,68,0.1)', borderRadius: 8, color: '#fca5a5', fontSize: 12 }}>
          {error}
        </div>
      )}

      {/* Rich PowerFLOW info from the whole simulator.o + decomposer.o + .simerr */}
      <PowerFlowInfoPanel details={jobData.details} onGenerateSpanwise={generateSpanwise} generating={generating} genError={genError} />

      {/* Stage cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {(jobData.stages || []).map((stage, idx) => {
          const isRunning = stage.status === 'running';
          const isCompleted = stage.status === 'completed';
          const isFailed = stage.status === 'failed';
          const borderColor = isRunning ? '#22d3ee'
                            : isCompleted ? '#16a34a44'
                            : isFailed ? '#7f1d1d'
                            : '#1e293b';
          const Icon = isCompleted ? CheckCircle
                     : isRunning ? Clock
                     : isFailed ? AlertCircle
                     : Circle;
          const iconColor = isCompleted ? '#22c55e'
                          : isRunning ? '#22d3ee'
                          : isFailed ? '#ef4444'
                          : '#6b7280';

          return (
            <div key={stage.name + idx} style={{
              backgroundColor: '#0f172a', borderRadius: 12,
              border: `1px solid ${borderColor}`, padding: 18,
              boxShadow: isRunning ? '0 0 12px rgba(34,211,238,0.15)' : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <Icon style={{
                  width: 22, height: 22, color: iconColor, flexShrink: 0, marginTop: 2,
                  animation: isRunning ? 'pulse-dot 1.5s ease-in-out infinite' : 'none',
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <h3 style={{ color: '#fff', fontSize: 15, fontWeight: 600, margin: 0 }}>
                        {stage.name}
                      </h3>
                      {stage.description && (
                        <p style={{ color: '#9ca3af', fontSize: 11, marginTop: 2 }}>{stage.description}</p>
                      )}
                    </div>
                    {stage.duration && (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#d1d5db', fontSize: 12, fontFamily: 'monospace' }}>{stage.duration}</div>
                        <div style={{ color: '#6b7280', fontSize: 10 }}>Duration</div>
                      </div>
                    )}
                  </div>

                  {/* Per-stage progress bar (only for running) */}
                  {isRunning && stage.progress !== undefined && stage.progress > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ color: '#9ca3af', fontSize: 11 }}>Progress</span>
                        <span style={{ color: '#22d3ee', fontSize: 12, fontFamily: 'monospace', fontWeight: 600 }}>
                          {stage.progress.toFixed(1)}%
                        </span>
                      </div>
                      <div style={{ height: 6, backgroundColor: '#1e293b', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', width: `${Math.min(stage.progress, 100)}%`,
                          background: 'linear-gradient(90deg, #3b82f6, #22d3ee)',
                          transition: 'width 1s ease-out',
                        }} />
                      </div>
                    </div>
                  )}

                  {/* Timestamps */}
                  {(stage.startTime || stage.endTime) && (
                    <div style={{ marginTop: 8, fontSize: 10, color: '#6b7280', fontFamily: 'monospace' }}>
                      {stage.startTime && <span>Started: {stage.startTime}</span>}
                      {stage.endTime && <span style={{ marginLeft: 12 }}>Ended: {stage.endTime}</span>}
                    </div>
                  )}

                  {/* Metrics */}
                  {stage.metrics && Object.keys(stage.metrics).length > 0 && (
                    <div style={{
                      display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                      gap: 8, marginTop: 12,
                    }}>
                      {Object.entries(stage.metrics).map(([k, v]) => (
                        <div key={k} style={{
                          backgroundColor: '#1e293b', borderRadius: 8, padding: '8px 10px',
                          border: '1px solid #1e293b',
                        }}>
                          <div style={{ color: '#6b7280', fontSize: 10, marginBottom: 2 }}>{k}</div>
                          <div style={{ color: '#d1d5db', fontSize: 11, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {String(v)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      </div>{/* end screen dashboard view */}

      {/* Professional PDF report — rendered only for printing */}
      <div className="print-only">
        <JobReport data={jobData} clusterDef={clusterDef} />
      </div>

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

// ===== Active / History toggle =====
function ModeToggle({ mode, onChange }) {
  const opt = (key, label) => (
    <button
      onClick={() => onChange(key)}
      style={{
        padding: '6px 18px', border: 'none', cursor: 'pointer', fontSize: 13,
        fontWeight: 600, borderRadius: 8,
        background: mode === key ? '#1d4ed8' : 'transparent',
        color: mode === key ? '#fff' : '#9ca3af', transition: 'background 0.15s',
      }}
    >{label}</button>
  );
  return (
    <div style={{
      display: 'inline-flex', gap: 4, backgroundColor: '#0d1424',
      border: '1px solid #1f2937', borderRadius: 10, padding: 4, alignSelf: 'flex-start',
    }}>
      {opt('active', 'Active')}
      {opt('history', 'History')}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    RUNNING: ['#22c55e', 'rgba(34,197,94,0.12)'],
    ENDED: ['#f87171', 'rgba(239,68,68,0.12)'],
    COMPLETED: ['#22d3ee', 'rgba(34,211,238,0.12)'],
  };
  const [c, bg] = map[status] || ['#9ca3af', 'rgba(148,163,184,0.12)'];
  return (
    <span style={{
      color: c, backgroundColor: bg, border: `1px solid ${c}55`,
      fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>{status || '—'}</span>
  );
}

function fmtDuration(a, b) {
  if (!a || !b) return '--';
  const s = Math.max(0, Math.floor(b - a));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
function fmtWhen(ts) {
  if (!ts) return '--';
  try {
    return new Date(ts * 1000).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '--'; }
}

// ---- History analysis helpers ----
const CMP_COLORS = ['#22d3ee', '#f59e0b', '#a78bfa', '#34d399', '#f472b6', '#60a5fa', '#fb7185', '#a3e635'];
const recDet = (r) => r.details || r;
function ldSeriesOf(r) {
  const conv = recDet(r).convergence || r.convergence || {};
  const dm = new Map((conv.drag || []).map(([t, v]) => [t, v]));
  const out = [];
  (conv.lift || []).forEach(([t, lv]) => { const dv = dm.get(t); if (dv && Math.abs(dv) > 1e-9) out.push([t, lv / dv]); });
  return out.sort((a, b) => a[0] - b[0]);
}
const finalOf = (s) => (s && s.length ? s[s.length - 1][1] : null);
const shortName = (r) => {
  const c = parseConfig(r.jobName);
  const bp = /back(\d+)/i.exec(r.jobName || '');
  return `M${c.mach || '?'} α${c.alpha || '?'}${bp ? ' back' + bp[1] : ''}`;
};
const CMP_METRICS = {
  'L/D': (r) => finalOf(ldSeriesOf(r)),
  'Lift (Fz)': (r) => (r.finalLift ?? finalOf((recDet(r).convergence || {}).lift)),
  'Drag (Fx)': (r) => (r.finalDrag ?? finalOf((recDet(r).convergence || {}).drag)),
  'Pitch (My)': (r) => finalOf((recDet(r).forceHistory || {}).pitchMoment),
  'Roll (Mx)': (r) => finalOf((recDet(r).forceHistory || {}).rollMoment),
  'Yaw (Mz)': (r) => finalOf((recDet(r).forceHistory || {}).yawMoment),
};
const CMP_PARAMS = {
  'Angle of attack (α)': (r) => { const c = parseConfig(r.jobName); return c.alpha ? parseFloat(c.alpha) : null; },
  'Mach': (r) => { const c = parseConfig(r.jobName); return c.mach ? parseFloat(c.mach) : null; },
  'Antenna position (back)': (r) => { const m = /back(\d+)/i.exec(r.jobName || ''); return m ? parseInt(m[1], 10) : null; },
};

function CompareView({ records, onBack }) {
  const merged = useMemo(() => {
    const m = new Map();
    records.forEach((r, idx) => ldSeriesOf(r).forEach(([t, v]) => {
      if (!m.has(t)) m.set(t, { ts: t }); m.get(t)['j' + idx] = v;
    }));
    return [...m.values()].sort((a, b) => a.ts - b.ts);
  }, [records]);
  const lines = records.map((r, idx) => ({ key: 'j' + idx, name: shortName(r), color: CMP_COLORS[idx % CMP_COLORS.length] }));
  const metricKeys = Object.keys(CMP_METRICS);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer', padding: 0, alignSelf: 'flex-start' }}>
        <ArrowLeft style={{ width: 16, height: 16 }} /> Back to History
      </button>
      <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 600, margin: 0 }}>Comparing {records.length} Jobs</h2>
      {/* side-by-side final-results table */}
      <div style={{ overflowX: 'auto', border: '1px solid #1e293b', borderRadius: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#0d1424' }}>
              <th style={{ textAlign: 'left', color: '#9ca3af', padding: '8px 10px', fontWeight: 600 }}>Metric</th>
              {records.map((r, i) => (
                <th key={i} style={{ textAlign: 'right', color: CMP_COLORS[i % CMP_COLORS.length], padding: '8px 10px', fontWeight: 700, whiteSpace: 'nowrap' }}>{shortName(r)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metricKeys.map((mk) => (
              <tr key={mk} style={{ borderTop: '1px solid #1e293b' }}>
                <td style={{ color: '#9ca3af', padding: '6px 10px' }}>{mk}</td>
                {records.map((r, i) => {
                  const v = CMP_METRICS[mk](r);
                  return <td key={i} style={{ textAlign: 'right', color: '#e5e7eb', fontFamily: 'monospace', padding: '6px 10px' }}>{v == null ? '—' : v.toFixed(mk === 'L/D' ? 2 : (mk.includes('(F') ? 4 : 2))}</td>;
                })}
              </tr>
            ))}
            <tr style={{ borderTop: '1px solid #1e293b' }}>
              <td style={{ color: '#9ca3af', padding: '6px 10px' }}>Status</td>
              {records.map((r, i) => <td key={i} style={{ textAlign: 'right', padding: '6px 10px' }}><StatusPill status={r.status} /></td>)}
            </tr>
          </tbody>
        </table>
      </div>
      {/* overlaid L/D convergence */}
      {merged.length > 1 && (
        <div style={{ backgroundColor: '#0b1322', borderRadius: 10, border: '1px solid #1e293b', padding: '12px 8px 4px', height: 300 }}>
          <div style={{ color: '#a78bfa', fontSize: 11, fontWeight: 700, marginBottom: 6, paddingLeft: 6 }}>L/D CONVERGENCE — OVERLAY</div>
          <ResponsiveContainer width="100%" height="88%">
            <LineChart data={merged} margin={{ top: 4, right: 14, bottom: 4, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="ts" stroke="#6b7280" tick={{ fontSize: 10 }} tickFormatter={(t) => t.toLocaleString()} />
              <YAxis stroke="#6b7280" tick={{ fontSize: 10 }} width={48} tickFormatter={(v) => v.toFixed(1)} />
              <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', fontSize: 12 }} labelStyle={{ color: '#9ca3af' }} labelFormatter={(t) => `timestep ${Number(t).toLocaleString()}`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {lines.map((l) => <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color} dot={false} strokeWidth={1.5} isAnimationActive={false} connectNulls />)}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function TrendsView({ records, onBack }) {
  const [param, setParam] = useState('Antenna position (back)');
  const [metric, setMetric] = useState('L/D');
  const data = useMemo(() => {
    const pts = [];
    records.forEach((r) => {
      const x = CMP_PARAMS[param](r);
      const y = CMP_METRICS[metric](r);
      if (x != null && y != null) pts.push({ x, y, name: shortName(r) });
    });
    return pts.sort((a, b) => a.x - b.x);
  }, [records, param, metric]);
  const sel = { background: '#0d1424', color: '#e5e7eb', border: '1px solid #334155', borderRadius: 8, padding: '6px 10px', fontSize: 13 };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer', padding: 0, alignSelf: 'flex-start' }}>
        <ArrowLeft style={{ width: 16, height: 16 }} /> Back to History
      </button>
      <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 600, margin: 0 }}>Design-Parameter Trends</h2>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ color: '#9ca3af', fontSize: 13 }}>Y (metric):
          <select value={metric} onChange={(e) => setMetric(e.target.value)} style={{ ...sel, marginLeft: 6 }}>
            {Object.keys(CMP_METRICS).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <label style={{ color: '#9ca3af', fontSize: 13 }}>X (parameter):
          <select value={param} onChange={(e) => setParam(e.target.value)} style={{ ...sel, marginLeft: 6 }}>
            {Object.keys(CMP_PARAMS).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
      </div>
      {data.length === 0 ? (
        <div style={{ padding: 32, color: '#6b7280', textAlign: 'center' }}>
          No jobs have both a parsable “{param}” and a “{metric}” value yet.
        </div>
      ) : (
        <div style={{ backgroundColor: '#0b1322', borderRadius: 10, border: '1px solid #1e293b', padding: '12px 8px 4px', height: 320 }}>
          <div style={{ color: '#22d3ee', fontSize: 11, fontWeight: 700, marginBottom: 6, paddingLeft: 6 }}>{metric} vs {param} ({data.length} run{data.length !== 1 ? 's' : ''})</div>
          <ResponsiveContainer width="100%" height="88%">
            <LineChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="x" type="number" domain={['dataMin', 'dataMax']} stroke="#6b7280" tick={{ fontSize: 10 }} />
              <YAxis stroke="#6b7280" tick={{ fontSize: 10 }} width={54} />
              <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', fontSize: 12 }} labelStyle={{ color: '#9ca3af' }}
                formatter={(v, n, p) => [Number(v).toFixed(3), p?.payload?.name || metric]} labelFormatter={(x) => `${param} = ${x}`} />
              <Line type="monotone" dataKey="y" name={metric} stroke="#22d3ee" strokeWidth={2} dot={{ r: 4 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div style={{ fontSize: 11, color: '#6b7280' }}>
        Parameters are parsed from job names (Mach, α, antenna “back” position). Trends populate as more runs are recorded.
      </div>
    </div>
  );
}

// ===== History panel: list + select→compare + trends =====
const HISTORY_PER_CLUSTER_INITIAL = 12;

function JobHistory({ onSelectJob }) {
  const { CLUSTERS, NODE_TO_CLUSTER } = useTopology();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [view, setView] = useState('list'); // list | compare | trends
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(() => new Set());   // cids collapsed
  const [showAll, setShowAll] = useState(() => new Set());        // cids showing all rows
  const [clusterFilter, setClusterFilter] = useState('all');     // 'all' | cluster id

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/jobs/history');
        if (cancelled) return;
        if (res.data?.success) { setRecords(res.data.jobs || []); setError(null); }
        else setError(res.data?.error || 'Failed to load history');
      } catch (e) {
        if (!cancelled) setError('Fetch error: ' + (e.response?.status || e.message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const keyOf = (r) => r.id || `${r.path || ''}::${r.jobName || ''}`;
  const toggle = (k) => setSelected((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const selectedRecords = records.filter((r) => selected.has(keyOf(r)));

  // Filter (name / node / conditions) then group by cluster, most-recent first.
  const { order, groups, matchCount } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? records.filter((r) => `${r.jobName || ''} ${r.node || ''}`.toLowerCase().includes(q))
      : records;
    const recency = (r) => r.endedTs || r.lastSeen || 0;
    const g = {};
    filtered.forEach((r) => {
      const cid = NODE_TO_CLUSTER[r.node] || 'other';
      (g[cid] = g[cid] || []).push(r);
    });
    Object.values(g).forEach((arr) => arr.sort((a, b) => recency(b) - recency(a)));
    const ord = [...Object.keys(CLUSTERS), 'other'].filter((cid) => g[cid] && g[cid].length);
    return { order: ord, groups: g, matchCount: filtered.length };
  }, [records, search, CLUSTERS, NODE_TO_CLUSTER]);

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Loading history…</div>;
  if (error) return <div style={{ padding: 32, color: '#f87171' }}>{error}</div>;
  if (records.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
        No recorded jobs yet.<br />
        <span style={{ fontSize: 12 }}>Jobs are recorded automatically as they run.</span>
      </div>
    );
  }
  if (view === 'compare') return <CompareView records={selectedRecords} onBack={() => setView('list')} />;
  if (view === 'trends') return <TrendsView records={records} onBack={() => setView('list')} />;

  const renderCard = (r) => {
    const k = keyOf(r);
    const checked = selected.has(k);
    return (
      <div key={k}
        style={{ backgroundColor: '#0f172a', borderRadius: 12, border: `1px solid ${checked ? '#1d4ed8' : '#1e293b'}`, padding: 14, display: 'flex', gap: 12 }}>
        <input type="checkbox" checked={checked} onChange={() => toggle(k)}
          onClick={(e) => e.stopPropagation()} style={{ marginTop: 3, cursor: 'pointer', width: 16, height: 16, flexShrink: 0 }} />
        <div style={{ flex: 1, cursor: 'pointer', minWidth: 0 }} onClick={() => onSelectJob(r)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <Layers style={{ width: 16, height: 16, color: '#3b82f6', flexShrink: 0 }} />
            <span style={{ color: '#fff', fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 'calc(100% - 300px)', minWidth: 150 }}>{r.jobName}</span>
            <ClusterBadge node={r.node} />
            <StatusPill status={r.status} />
            <StallBadge lastActivityTs={r.lastActivityTs} status={r.status} />
            <WarningBadge count={r.errorCount} />
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: '#22d3ee' }}>{(r.overallProgress ?? 0).toFixed(1)}%</span>
          </div>
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>
            <span>timestep {r.lastTimestep != null ? r.lastTimestep.toLocaleString() : '--'}{r.case?.['Total Timesteps'] ? ` / ${r.case['Total Timesteps']}` : ''}</span>
            <span>Fz {r.finalLift != null ? r.finalLift.toFixed(4) : '--'}</span>
            <span>Fx {r.finalDrag != null ? r.finalDrag.toFixed(4) : '--'}</span>
            <span>errors {(r.errorCount || 0).toLocaleString()}</span>
            <span>ran {fmtDuration(r.firstSeen, r.endedTs || r.lastSeen)}</span>
            <span>last seen {fmtWhen(r.endedTs || r.lastSeen)}</span>
            {r.lingeredSec > 0 && (
              <span style={{ color: '#fbbf24' }} title="Finished/hung but stayed in the queue this long before it was cleared — not counted in run time">
                ⚠ lingered {fmtDuration(r.endedTs, r.reportedEndedTs)} after finishing
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ color: '#fff', fontSize: 18, fontWeight: 600, margin: 0 }}>
          {records.length} Recorded Job{records.length !== 1 ? 's' : ''}
        </h2>
        <div style={{ position: 'relative', minWidth: 220, flex: '1 1 240px', maxWidth: 420 }}>
          <Search style={{ position: 'absolute', left: 10, top: 9, width: 15, height: 15, color: '#64748b' }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search jobs (name, Mach, α, node)…"
            style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px 8px 32px', borderRadius: 8, border: '1px solid #1e293b', backgroundColor: '#0b1322', color: '#e5e7eb', fontSize: 13 }} />
        </div>
        <button onClick={() => setView('compare')} disabled={selectedRecords.length < 2}
          style={{ background: selectedRecords.length < 2 ? '#1f2937' : '#1d4ed8', color: selectedRecords.length < 2 ? '#6b7280' : '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: selectedRecords.length < 2 ? 'default' : 'pointer' }}>
          Compare ({selectedRecords.length})
        </button>
        <button onClick={() => setView('trends')}
          style={{ background: '#0d1424', color: '#d1d5db', border: '1px solid #334155', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Trends
        </button>
      </div>

      {/* Cluster filter — jump straight to one cluster's history */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', overflowX: 'auto', paddingBottom: 2 }}>
        {[
          { id: 'all', label: 'All clusters', color: '#94a3b8', count: matchCount },
          ...Object.keys(CLUSTERS).map((cid) => ({
            id: cid, label: CLUSTERS[cid].label, color: CLUSTERS[cid].color, count: groups[cid]?.length || 0,
          })),
          ...(groups.other?.length ? [{ id: 'other', label: 'Other', color: '#64748b', count: groups.other.length }] : []),
        ].map((c) => {
          const active = clusterFilter === c.id;
          return (
            <button key={c.id} onClick={() => setClusterFilter(c.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, flexShrink: 0,
                background: active ? `${c.color}26` : '#0d1424',
                border: `1px solid ${active ? c.color : '#334155'}`,
                color: active ? '#fff' : '#cbd5e1',
                borderRadius: 999, padding: '6px 13px', fontSize: 13,
                fontWeight: active ? 700 : 500, cursor: 'pointer',
              }}>
              {c.id !== 'all' && <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.color, flexShrink: 0 }} />}
              {c.label}
              <span style={{
                background: active ? c.color : '#1e293b', color: active ? '#0b1322' : '#94a3b8',
                borderRadius: 999, padding: '0 7px', fontSize: 11, fontWeight: 700, minWidth: 18, textAlign: 'center',
              }}>{c.count}</span>
            </button>
          );
        })}
      </div>

      {search && <div style={{ color: '#6b7280', fontSize: 12 }}>{matchCount} match{matchCount !== 1 ? 'es' : ''}</div>}
      {matchCount === 0 && <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>No jobs match “{search}”.</div>}
      {matchCount > 0 && clusterFilter !== 'all' && !(groups[clusterFilter]?.length) && (
        <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
          No history in {CLUSTERS[clusterFilter]?.label || clusterFilter}{search ? ` matching “${search}”` : ''}.
        </div>
      )}

      {/* Cluster sections */}
      {(clusterFilter === 'all' ? order : order.filter((cid) => cid === clusterFilter)).map((cid) => {
        const arr = groups[cid];
        const cl = CLUSTERS[cid];
        const color = cl?.color || '#64748b';
        const label = cl?.label || 'Other / Unassigned';
        const focused = clusterFilter === cid;   // drilled into this one cluster
        const isCollapsed = collapsed.has(cid) && !focused;
        const seeAll = showAll.has(cid) || focused;
        const shown = seeAll ? arr : arr.slice(0, HISTORY_PER_CLUSTER_INITIAL);
        return (
          <div key={cid}>
            <div onClick={() => setCollapsed((s) => { const n = new Set(s); n.has(cid) ? n.delete(cid) : n.add(cid); return n; })}
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 10px', borderRadius: 8, background: `${color}14`, borderLeft: `3px solid ${color}`, marginBottom: 8 }}>
              {isCollapsed ? <ChevronRight style={{ width: 16, height: 16, color }} /> : <ChevronDown style={{ width: 16, height: 16, color }} />}
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{label}</span>
              <span style={{ color, fontSize: 12, fontWeight: 600 }}>{arr.length} job{arr.length !== 1 ? 's' : ''}</span>
            </div>
            {!isCollapsed && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                {shown.map(renderCard)}
                {!seeAll && arr.length > HISTORY_PER_CLUSTER_INITIAL && (
                  <button onClick={() => setShowAll((s) => new Set(s).add(cid))}
                    style={{ alignSelf: 'flex-start', background: '#0d1424', color: '#93c5fd', border: '1px solid #334155', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    Show {arr.length - HISTORY_PER_CLUSTER_INITIAL} more in {label}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


// Fires a compliant test notification (no controlled data) via the backend,
// which uses the operator-set RESEND_API_KEY. Lets you verify email delivery.
function TestEmailButton() {
  const [status, setStatus] = useState(null); // null | sending | ok | err
  const [msg, setMsg] = useState('');
  const send = async () => {
    setStatus('sending'); setMsg('');
    try {
      const res = await axios.post('/api/notify/test');
      if (res.data?.success) { setStatus('ok'); setMsg(res.data.detail || 'sent'); }
      else { setStatus('err'); setMsg(res.data?.detail || 'failed'); }
    } catch (e) { setStatus('err'); setMsg(e.response?.data?.detail || e.message); }
    setTimeout(() => setStatus((s) => (s === 'sending' ? s : null)), 7000);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {status === 'ok' && <span style={{ color: '#22c55e', fontSize: 12 }}>✓ {msg}</span>}
      {status === 'err' && <span style={{ color: '#f87171', fontSize: 12 }}>{msg}</span>}
      <button onClick={send} disabled={status === 'sending'}
        title="Send a test completion email (uses the RESEND_API_KEY set on the server)"
        style={{ background: '#0d1424', color: '#d1d5db', border: '1px solid #334155', borderRadius: 8, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: status === 'sending' ? 'default' : 'pointer' }}>
        {status === 'sending' ? 'Sending…' : 'Send test email'}
      </button>
    </div>
  );
}

// ==================== MAIN COMPONENT WITH CACHING ====================
const JobManagement = ({ openJob, onOpenConsumed }) => {
  const [view, setView] = useState('list');
  const [mode, setMode] = useState('active');
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobsCache, setJobsCache] = useState([]);
  // Live job data comes from the shared /api/snapshot poll (DataProvider).
  const { jobsLive: liveData } = useData();

  // Requested from elsewhere (Dashboard running-job click, or an email deep link)
  // → open that job's detail.
  useEffect(() => {
    if (!openJob) return;
    setMode('active');
    setSelectedJob(openJob);
    setView('detail');
    onOpenConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openJob]);

  if (view === 'detail' && selectedJob) {
    return (
      <JobDetail
        job={selectedJob}
        onBack={() => setView('list')}
        liveData={liveData}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <ModeToggle mode={mode} onChange={setMode} />
        <TestEmailButton />
      </div>
      {mode === 'active' ? (
        <JobsList
          onSelectJob={(j) => { setSelectedJob(j); setView('detail'); }}
          onCacheUpdate={setJobsCache}
          cachedJobs={jobsCache}
          liveData={liveData}
        />
      ) : (
        <JobHistory
          onSelectJob={(j) => { setSelectedJob({ ...j, fromArchive: true }); setView('detail'); }}
        />
      )}
    </div>
  );
};

export default JobManagement;
